/**
 * Daewoo-aligned finance (AGEON frontend).
 * Keep in sync with `backend/utils/daewooFinance.js`.
 */

export const DEFAULT_PAYMENT_SETTINGS = {
    loiPercent: 25,
    agreementPercent: 100,
    loiCalculationType: 'deal_amount',
    agreementCalculationType: 'deal_amount',
    filterUsersByLocation: false,
    franchiseAvailableThresholdPercent: 10,
    franchiseBookedThresholdPercent: 25,
    paymentAgingWarningDays: 45,
    defaultDealType: 'online',
};

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

function applyTaxRounding(amount, mode) {
    const n = Number(amount);
    if (!Number.isFinite(n)) return 0;
    if (mode === 'truncate2dp') return Math.trunc(n * 100) / 100;
    return Math.round(n);
}

export function calculateFinanceSnapshot({
    dealAmount,
    dealType,
    isExempted,
    whiteAmount,
    gstRate = 18,
    tdsRate,
    gstBaseOverride,
    taxRounding = 'round',
}) {
    let gstBase;
    let isManualGstBase = false;

    if (gstBaseOverride !== undefined && gstBaseOverride !== null && gstBaseOverride !== '') {
        gstBase = Number(gstBaseOverride);
        isManualGstBase = true;
        if (Number.isNaN(gstBase) || gstBase < 0) {
            gstBase = dealType === 'online' ? dealAmount : whiteAmount;
            isManualGstBase = false;
        }
    } else {
        gstBase = dealType === 'online' ? dealAmount : whiteAmount;
        isManualGstBase = false;
    }

    const gstAmount = applyTaxRounding((gstBase * gstRate) / 100, taxRounding);
    const tdsBase = isExempted ? 0 : (isManualGstBase ? gstBase : whiteAmount);
    const tdsAmount = isExempted ? 0 : applyTaxRounding((tdsBase * tdsRate) / 100, taxRounding);
    const cashAmount = dealType === 'cash' ? dealAmount - whiteAmount : 0;
    const finalPayableAmount =
        dealType === 'online'
            ? dealAmount + gstAmount - tdsAmount
            : cashAmount + (whiteAmount + gstAmount - tdsAmount);

    return {
        dealAmount,
        dealType,
        isExempted,
        whiteAmount,
        gstRate,
        gstBase,
        gstAmount,
        isManualGstBase,
        gstBaseOverride: isManualGstBase ? gstBase : undefined,
        tdsRate,
        tdsBase,
        tdsAmount,
        finalPayableAmount,
        challanRequired: tdsAmount > 0,
        calculatedAt: new Date(),
    };
}

function mergePayment(paymentSettings) {
    return { ...DEFAULT_PAYMENT_SETTINGS, ...(paymentSettings || {}) };
}

function mergeFinance(finance) {
    return {
        defaultGstPercent: num(finance?.defaultGstPercent ?? 18) || 18,
        defaultTdsPercent: num(finance?.defaultTdsPercent ?? 2) || 2,
        gstTdsRounding: finance?.gstTdsRounding === 'truncate2dp' ? 'truncate2dp' : 'round',
    };
}

/** Resolve deal value from dynamic registration fields (same keys as financeLedger). */
export function submissionDealAmount(data) {
    if (!data || typeof data !== 'object') return 0;
    const keys = ['totalDealValue', 'dealValue', 'dealAmount', 'budgetINR', 'franchiseFee', 'investmentAmount', 'projectValue'];
    for (const k of keys) {
        const v = data[k];
        if (v != null && v !== '') {
            const n = num(v);
            if (n > 0) return n;
        }
    }
    return 0;
}

export function loiThresholdAmount(dealAmount, finalPayableAmount, paymentSettings = {}) {
    const p = mergePayment(paymentSettings);
    const pct = num(p.loiPercent);
    const base = p.loiCalculationType === 'final_payable' ? num(finalPayableAmount) : num(dealAmount);
    return Number(((pct / 100) * base).toFixed(2));
}

export function agreementThresholdAmount(dealAmount, finalPayableAmount, paymentSettings = {}) {
    const p = mergePayment(paymentSettings);
    const pct = num(p.agreementPercent);
    const base = p.agreementCalculationType === 'final_payable' ? num(finalPayableAmount) : num(dealAmount);
    return Number(((pct / 100) * base).toFixed(2));
}

/**
 * Full projection for a form `data` object + admin settings.
 * `extras.secondPaymentsApprovedSum` — approved instalments stored on FormSubmission (Daewoo second payments).
 */
export function submissionFinancePreview(data = {}, finance = {}, paymentSettings = {}, extras = {}) {
    const fin = mergeFinance(finance);
    const pay = mergePayment(paymentSettings);

    const dealAmount = submissionDealAmount(data);
    const dealType = (data.dealType || pay.defaultDealType || 'online').toLowerCase() === 'cash' ? 'cash' : 'online';
    const isExempted = Boolean(data.isExempted ?? data.tdsExempted);
    const whiteAmount = dealType === 'cash' ? num(data.whiteAmount || dealAmount) : dealAmount;
    const gstRate = num(data.gstRate ?? data.gstPercent ?? fin.defaultGstPercent);
    let tdsRate = data.tdsRate;
    if (tdsRate === undefined || tdsRate === null || tdsRate === '') tdsRate = fin.defaultTdsPercent;
    tdsRate = num(tdsRate);
    if (isExempted) tdsRate = 0;

    const snapshot = calculateFinanceSnapshot({
        dealAmount,
        dealType,
        isExempted,
        whiteAmount,
        gstRate,
        tdsRate,
        gstBaseOverride: data.gstBaseOverride,
        taxRounding: fin.gstTdsRounding === 'truncate2dp' ? 'truncate2dp' : 'round',
    });

    const token = num(data.tokenReceivedAmount ?? data.tokenReceived ?? data.tokenAmount);
    const secondApproved = num(extras.secondPaymentsApprovedSum);
    const totalReceivedTowardPayable = token + secondApproved;
    const fp = num(snapshot.finalPayableAmount);
    const receivedPct = fp > 0 ? Math.min(100, Number(((totalReceivedTowardPayable / fp) * 100).toFixed(2))) : 0;

    return {
        dealAmount,
        snapshot,
        tokenReceived: token,
        secondPaymentsApprovedSum: secondApproved,
        totalReceivedTowardPayable,
        receivedPercent: receivedPct,
        remainPercent: Number(Math.max(0, 100 - receivedPct).toFixed(2)),
        balanceAfterToken: Number(Math.max(0, fp - totalReceivedTowardPayable).toFixed(2)),
        loiThreshold: loiThresholdAmount(dealAmount, snapshot.finalPayableAmount, pay),
        agreementThreshold: agreementThresholdAmount(dealAmount, snapshot.finalPayableAmount, pay),
        paymentSettings: pay,
    };
}
